import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * The (non-standardised but widely supported) event fired by Chromium based
 * browsers when a site meets the installability criteria.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface Navigator {
    readonly standalone?: boolean;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PwaInstallService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  /** Emits true while the app can be installed through our own button. */
  readonly installable$ = new BehaviorSubject<boolean>(false);

  /** Synchronous snapshot of {@link installable$}. */
  get installableValue(): boolean {
    return this.installable$.value;
  }

  constructor() {
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      // Prevent the default mini-infobar so we can offer our own button.
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.installable$.next(true);
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.installable$.next(false);
    });
  }

  /** True when the app is already running as an installed PWA. */
  get isRunningStandalone(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari
      window.navigator.standalone === true
    );
  }

  /** True on iPhone / iPad / iPod, where there is no programmatic install API. */
  get isIos(): boolean {
    const ua = window.navigator.userAgent;
    const iOsDevice = /iPad|iPhone|iPod/.test(ua);
    // iPadOS 13+ reports as a Mac but is a touch device.
    const iPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    return iOsDevice || iPadOs;
  }

  /**
   * Whether to offer the iOS "Add to Home Screen" instructions: an iOS device
   * that is not already running the installed app.
   */
  get showIosInstall(): boolean {
    return this.isIos && !this.isRunningStandalone;
  }

  /**
   * True when any install affordance should be shown: either the browser
   * supports our programmatic prompt, or we can guide an iOS user.
   */
  get canOfferInstall(): boolean {
    return (this.installableValue && !this.isRunningStandalone) || this.showIosInstall;
  }

  /**
   * Triggers the native install prompt. Returns true if the user accepted.
   */
  async install(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }
    const prompt = this.deferredPrompt;
    // The prompt can only be used once.
    this.deferredPrompt = null;
    this.installable$.next(false);

    await prompt.prompt();
    const choice = await prompt.userChoice;
    return choice.outcome === 'accepted';
  }
}
